import React, { useState, useCallback } from "react";
import { Pressable, View } from "react-native";
import color from "@constants/color";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import { riderTypeStyles as styles } from "./styles";
import { useOnboarding } from "./OnboardingContext";
import { useFlowConfig } from "./FlowConfigContext";
import { useActiveFlow } from "./ActiveFlowContext";
import { findFirstStep, DEFAULT_FLOW_CONDITIONS } from "./flowConditions";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { DbTypeConfig } from "./flowConfig.types";
import type { OnboardingStackParamList } from "./types";

interface RiderTypeProps {
  navigation: StackNavigationProp<OnboardingStackParamList, "RiderType">;
}

const RiderType: React.FC<RiderTypeProps> = ({ navigation }) => {
  const { updateData } = useOnboarding();
  const { flowConfig } = useFlowConfig();
  const { setActiveFlow } = useActiveFlow();
  const [selected, setSelected] = useState<DbTypeConfig | null>(null);

  const dbTypes = flowConfig.db_types;

  const onContinue = useCallback(() => {
    const chosen = selected ?? dbTypes[0];
    if (!chosen) return;

    // Activate the selected type's step sequence
    setActiveFlow(chosen.id, chosen.steps);
    // Mirror riderType into OnboardingContext so legacy fields (submitOnboarding) still work
    updateData({ riderType: chosen.id === "LAGGAN" ? "laggan" : "normal" });

    const firstStep = findFirstStep(chosen.steps, DEFAULT_FLOW_CONDITIONS);
    if (firstStep) {
      navigation.navigate(firstStep.id as never);
    }
  }, [selected, dbTypes, setActiveFlow, updateData, navigation]);

  return (
    <>
      <HeaderWithBackAndTitle
        headertitle="Select Rider Type"
        navigation={navigation}
        hideShadow
      />

      <View style={styles.card}>
        <Text variant="title_18" style={styles.title}>
          Select Delivery Boy Type
        </Text>
        <Text variant="body_text_3" style={styles.subtitle}>
          Please select the type that applies to you
        </Text>

        {dbTypes.map((dbType) => {
          const isSelected = selected?.id === dbType.id;
          return (
            <Pressable
              key={dbType.id}
              style={[
                styles.radioOption,
                isSelected && styles.radioOptionSelected,
              ]}
              onPress={() => setSelected(dbType)}
              accessibilityRole="radio"
              accessibilityState={{ selected: isSelected }}
              accessibilityLabel={dbType.label}
            >
              <View
                style={[
                  styles.radioCircle,
                  isSelected && styles.radioCircleSelected,
                ]}
              >
                {isSelected && <View style={styles.radioCircleInner} />}
              </View>
              <Text
                variant="body_text_2"
                style={[
                  styles.radioLabel,
                  isSelected && styles.radioLabelSelected,
                ]}
              >
                {dbType.label}
              </Text>
            </Pressable>
          );
        })}

        <Pressable
          style={styles.continueButton}
          disabled={false}
          onPress={onContinue}
          accessibilityRole="button"
          accessibilityLabel="Continue"
        >
          <Text variant="title_16" style={styles.continueButtonText}>
            Continue
          </Text>
        </Pressable>
      </View>
    </>
  );
};

export default RiderType;
